import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Download,
  FileSpreadsheet,
  Wallet,
  ChevronLeft,
  ChevronRight,
  Calendar,
  TrendingUp,
  Pencil,
  X as XIcon,
  Save,
  Loader2
} from 'lucide-react';
import { Card } from '../components/ui';
import { usePayrollData, MONTHS_FR, type FilterMode } from '../hooks/usePayrollData';
import { usePayrollActions } from '../hooks/usePayrollActions';
import { TimesheetAuditTrail } from '../components/TimesheetAuditTrail';

export default function SecretariatPayroll() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    rows,
    loading,
    filterMode,
    setFilterMode,
    selectedYear,
    setSelectedYear,
    selectedMonth,
    setSelectedMonth,
    selectedWeek,
    setSelectedWeek,
    startDate,
    endDate,
    periodLabel,
    dbPeriodStr,
    handlePrevious,
    handleNext,
    handleToday,
    refresh,
    totalEmployees,
    totalHoursAll,
    totalOvertimeAll,
    totalOvertime25All,
    totalOvertime50All,
    totalSundayAll,
    totalPaniersAll,
    totalGDDaysAll,
    totalGDAmountAll,
    totalGDTravelHoursAll,
  } = usePayrollData();

  const {
    exportPDF,
    exportExcel,
    correctionEmployee,
    setCorrectionEmployee,
    correctionEntries,
    correctionEdits,
    savingCorrection,
    openCorrection,
    updateCorrectionField,
    saveCorrections,
    getMergedEntry,
    hasEdits,
  } = usePayrollActions({
    rows,
    startDate,
    endDate,
    periodLabel,
    dbPeriodStr,
    userId: user?.id,
    refresh,
  });

  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* HEADER */}
      <Card style={{ padding: '16px 24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <button
              onClick={() => navigate('/secretariat')}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: '#f1f5f9', border: 'none', color: '#64748b',
                cursor: 'pointer', fontWeight: '500', padding: '8px 12px',
                borderRadius: '8px', fontSize: '0.85rem'
              }}
            >
              <ChevronLeft size={18} /> Retour
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ padding: '10px', background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', borderRadius: '12px', color: 'white' }}>
                <Wallet size={22} />
              </div>
              <div>
                <h1 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1e293b', margin: 0 }}>Gestion de la Paie</h1>
                <p style={{ color: '#64748b', margin: 0, fontSize: '0.8rem' }}>Feuilles validées uniquement</p>
              </div>
            </div>
          </div>

          {/* Boutons d'export */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={exportPDF}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'white', color: '#dc2626',
                border: '1px solid #fecaca', padding: '8px 16px',
                borderRadius: '8px', cursor: 'pointer', fontWeight: '600',
                fontSize: '0.85rem', transition: 'all 0.2s'
              }}
            >
              <Download size={16} /> PDF
            </button>
            <button
              onClick={exportExcel}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white', border: 'none', padding: '8px 16px',
                borderRadius: '8px', cursor: 'pointer', fontWeight: '600',
                fontSize: '0.85rem', boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
              }}
            >
              <FileSpreadsheet size={16} /> Excel
            </button>
          </div>
        </div>
      </Card>

      <div>

        {/* SECTION FILTRES + STATS */}
        <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: '20px', marginBottom: '24px' }}>

          {/* Carte Filtre de Date */}
          <Card shadow="md">
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Période</h3>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>Sélectionnez la période à analyser</p>
            </div>

            {/* Sélecteur de mode */}
            <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', padding: '4px', borderRadius: '10px', marginBottom: '12px' }}>
              {(['week', 'month', 'year'] as FilterMode[]).map(mode => (
                <button
                  key={mode}
                  onClick={() => setFilterMode(mode)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.8rem',
                    background: filterMode === mode ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' : 'transparent',
                    color: filterMode === mode ? 'white' : '#64748b',
                    transition: 'all 0.2s',
                    boxShadow: filterMode === mode ? '0 2px 4px rgba(79, 70, 229, 0.3)' : 'none'
                  }}
                >
                  {mode === 'week' ? 'Semaine' : mode === 'month' ? 'Mois' : 'Année'}
                </button>
              ))}
            </div>

            {/* Navigation */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: '#f8fafc', padding: '12px', borderRadius: '10px', marginBottom: '12px'
            }}>
              <button
                onClick={handlePrevious}
                style={{
                  padding: '8px', background: 'white', border: '1px solid #e2e8f0',
                  borderRadius: '8px', cursor: 'pointer', color: '#64748b',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <ChevronLeft size={18} />
              </button>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <Calendar size={16} color="#4f46e5" />
                  <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.9rem' }}>{periodLabel}</span>
                </div>
              </div>
              <button
                onClick={handleNext}
                style={{
                  padding: '8px', background: 'white', border: '1px solid #e2e8f0',
                  borderRadius: '8px', cursor: 'pointer', color: '#64748b',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Sélecteurs détaillés */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{
                  flex: 1, padding: '10px', borderRadius: '8px',
                  border: '1px solid #e2e8f0', fontSize: '0.85rem',
                  cursor: 'pointer', background: 'white'
                }}
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>

              {(filterMode === 'month' || filterMode === 'week') && (
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  style={{
                    flex: 2, padding: '10px', borderRadius: '8px',
                    border: '1px solid #e2e8f0', fontSize: '0.85rem',
                    cursor: 'pointer', background: 'white'
                  }}
                >
                  {MONTHS_FR.map((m, idx) => (
                    <option key={idx} value={idx}>{m}</option>
                  ))}
                </select>
              )}

              {filterMode === 'week' && (
                <select
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
                  style={{
                    flex: 1, padding: '10px', borderRadius: '8px',
                    border: '1px solid #e2e8f0', fontSize: '0.85rem',
                    cursor: 'pointer', background: 'white'
                  }}
                >
                  {Array.from({ length: 53 }, (_, i) => i + 1).map(w => (
                    <option key={w} value={w}>S{w}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Bouton Aujourd'hui */}
            <button
              onClick={handleToday}
              style={{
                width: '100%',
                padding: '10px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                color: '#15803d',
                fontWeight: '600',
                fontSize: '0.85rem',
                cursor: 'pointer'
              }}
            >
              Aujourd'hui
            </button>
          </Card>

          {/* Cartes Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px' }}>
            {/* Employés */}
            <div style={{
              background: 'white', borderRadius: '16px', padding: '20px',
              border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Employés
              </div>
              <div style={{ fontSize: '2rem', fontWeight: '800', color: '#1e293b' }}>
                {loading ? '-' : totalEmployees}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
                sur cette période
              </div>
            </div>

            {/* Heures totales */}
            <div style={{
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              borderRadius: '16px', padding: '20px', color: 'white',
              boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.3)'
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '600', opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Heures Totales
              </div>
              <div style={{ fontSize: '2rem', fontWeight: '800' }}>
                {loading ? '-' : totalHoursAll.toFixed(2)}
              </div>
              <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '4px' }}>
                heures travaillées
              </div>
            </div>

            {/* Heures Supp */}
            <div style={{
              background: 'white', borderRadius: '16px', padding: '20px',
              border: '1px solid #fde68a', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <TrendingUp size={16} color="#d97706" />
                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  H. Supp
                </span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: '800', color: '#d97706' }}>
                {loading ? '-' : (totalOvertimeAll + totalSundayAll) > 0 ? `+${(totalOvertimeAll + totalSundayAll).toFixed(2)}` : '0'}
              </div>
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '6px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {totalOvertime25All > 0 && <span style={{ background: '#fef3c7', padding: '2px 6px', borderRadius: '4px', color: '#92400e' }}>{totalOvertime25All.toFixed(2)}h 25%</span>}
                {totalOvertime50All > 0 && <span style={{ background: '#fee2e2', padding: '2px 6px', borderRadius: '4px', color: '#991b1b' }}>{totalOvertime50All.toFixed(2)}h 50%</span>}
                {totalSundayAll > 0 && <span style={{ background: '#dbeafe', padding: '2px 6px', borderRadius: '4px', color: '#1e40af' }}>{totalSundayAll.toFixed(2)}h dim.</span>}
              </div>
            </div>

            {/* Paniers */}
            <div style={{
              background: 'white', borderRadius: '16px', padding: '20px',
              border: '1px solid #bfdbfe', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Paniers
              </div>
              <div style={{ fontSize: '2rem', fontWeight: '800', color: '#2563eb' }}>
                {loading ? '-' : totalPaniersAll}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
                repas comptabilisés
              </div>
            </div>

            {/* Grands Déplacements */}
            <div style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              borderRadius: '16px', padding: '20px', color: 'white',
              boxShadow: '0 4px 6px -1px rgba(220, 38, 38, 0.3)'
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '600', opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Grands Dépl.
              </div>
              <div style={{ fontSize: '2rem', fontWeight: '800' }}>
                {loading ? '-' : totalGDDaysAll}
              </div>
              <div style={{ fontSize: '0.7rem', opacity: 0.85, marginTop: '4px' }}>
                {!loading && totalGDDaysAll > 0 && (
                  <span style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 6px', borderRadius: '4px' }}>{totalGDAmountAll.toFixed(2)} €</span>
                    {totalGDTravelHoursAll > 0 && <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 6px', borderRadius: '4px' }}>{totalGDTravelHoursAll}h route</span>}
                  </span>
                )}
                {!loading && totalGDDaysAll === 0 && <span>jours de découché</span>}
              </div>
            </div>
          </div>
        </div>

        {/* TABLEAU */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{
            padding: '16px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
              Détail par collaborateur
            </h2>
            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
              {rows.length} résultat{rows.length > 1 ? 's' : ''}
            </span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '14px 24px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Collaborateur</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Heures</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', color: '#b45309', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>H. Supp</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', color: '#0891b2', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Trajets</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', color: '#0891b2', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Transp.</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', color: '#2563eb', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Paniers</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#dc2626', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>G. Dépl.</th>
                  <th style={{ padding: '14px 24px', textAlign: 'left', fontWeight: '600', color: '#7c3aed', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Zones</th>
                  <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '600', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTopColor: '#4f46e5', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                        Chargement en cours...
                      </div>
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: '60px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', color: '#64748b' }}>
                        <Wallet size={40} strokeWidth={1.5} />
                        <div>
                          <p style={{ margin: 0, fontWeight: '600' }}>Aucune donnée</p>
                          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>Aucune feuille validée sur cette période</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : rows.map((row, idx) => (
                  <tr
                    key={row.empId}
                    style={{
                      borderBottom: idx < rows.length - 1 ? '1px solid #f1f5f9' : 'none',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                  >
                    <td style={{ padding: '16px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '10px',
                          background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: '700', color: '#4f46e5', fontSize: '0.85rem'
                        }}>
                          {row.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>{row.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{row.role}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <span style={{ fontWeight: '700', fontSize: '1rem', color: '#1e293b' }}>{row.totalHours.toFixed(2)}</span>
                      <span style={{ color: '#94a3b8', fontSize: '0.8rem', marginLeft: '2px' }}>h</span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      {(row.overtime > 0 || row.sundayHours > 0) ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                          <span style={{
                            background: '#fef3c7', color: '#b45309',
                            padding: '4px 10px', borderRadius: '6px',
                            fontWeight: '600', fontSize: '0.85rem'
                          }}>
                            +{(row.overtime + row.sundayHours).toFixed(2)}h
                          </span>
                          <div style={{ display: 'flex', gap: '6px', fontSize: '0.7rem' }}>
                            {row.overtime25 > 0 && <span style={{ color: '#92400e' }}>{row.overtime25.toFixed(2)}h 25%</span>}
                            {row.overtime50 > 0 && <span style={{ color: '#991b1b' }}>{row.overtime50.toFixed(2)}h 50%</span>}
                            {row.sundayHours > 0 && <span style={{ color: '#1e40af' }}>{row.sundayHours.toFixed(2)}h dim</span>}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#cbd5e1' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right', color: '#475569', fontWeight: '500' }}>{row.trajet}</td>
                    <td style={{ padding: '16px', textAlign: 'right', color: '#475569', fontWeight: '500' }}>{row.transport}</td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <span style={{ fontWeight: '700', color: '#2563eb', fontSize: '0.95rem' }}>{row.panier}</span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      {row.gdDaysCount > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                          <span style={{
                            background: '#fef2f2', color: '#dc2626',
                            padding: '4px 10px', borderRadius: '6px',
                            fontWeight: '700', fontSize: '0.9rem'
                          }}>
                            {row.gdDaysCount} j
                          </span>
                          <div style={{ fontSize: '0.7rem', color: '#b91c1c' }}>
                            {row.gdTotalAmount > 0 && <span>{row.gdTotalAmount.toFixed(2)}€</span>}
                            {row.gdTotalTravelHours > 0 && <span> · {row.gdTotalTravelHours}h</span>}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#cbd5e1' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '16px 24px' }}>
                      {row.zoneDetails ? (
                        <span style={{
                          background: 'linear-gradient(135deg, #f3e8ff 0%, #ede9fe 100%)',
                          color: '#7c3aed',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '0.8rem',
                          display: 'inline-block'
                        }}>
                          {row.zoneDetails}
                        </span>
                      ) : (
                        <span style={{ color: '#cbd5e1' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <button
                        onClick={() => openCorrection(row.empId, row.name)}
                        style={{
                          background: 'none', border: '1px solid #e2e8f0', borderRadius: '8px',
                          padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center',
                          gap: '4px', fontSize: '0.75rem', color: '#64748b', transition: 'all 0.15s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#3b82f6'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; }}
                        title="Corriger les données de cet employé"
                      >
                        <Pencil size={14} /> Corriger
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* MODAL DE CORRECTION */}
      {correctionEmployee && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => !savingCorrection && setCorrectionEmployee(null)}>
          <div
            style={{
              background: 'white', borderRadius: '16px', width: '90vw', maxWidth: '1000px',
              maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
              boxShadow: '0 25px 50px rgba(0,0,0,0.15)'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid #e2e8f0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>
                  Correction — {correctionEmployee.name}
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                  {periodLabel} · Modifiez les valeurs puis enregistrez
                </p>
              </div>
              <button onClick={() => setCorrectionEmployee(null)} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '8px',
                borderRadius: '8px', color: '#64748b'
              }}>
                <XIcon size={20} />
              </button>
            </div>

            {/* Table */}
            <div style={{ overflow: 'auto', flex: 1, padding: '0' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '0.75rem' }}>Date</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600', color: '#475569', fontSize: '0.75rem' }} title="Normales / Samedi / Majorées">Heures (N/Sa/M)</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600', color: '#475569', fontSize: '0.75rem' }}>Paniers</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600', color: '#475569', fontSize: '0.75rem' }}>Trajets</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600', color: '#475569', fontSize: '0.75rem' }}>Transp.</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600', color: '#475569', fontSize: '0.75rem' }}>Zone</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600', color: '#dc2626', fontSize: '0.75rem' }}>GD</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600', color: '#dc2626', fontSize: '0.75rem' }}>GD €</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600', color: '#dc2626', fontSize: '0.75rem' }}>GD Route</th>
                  </tr>
                </thead>
                <tbody>
                  {correctionEntries.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                        Chargement...
                      </td>
                    </tr>
                  ) : correctionEntries.map(entry => {
                    const merged = getMergedEntry(entry);
                    const isEdited = !!correctionEdits[entry.id];
                    const dayName = new Date(entry.work_date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
                    return (
                      <tr key={entry.id} style={{
                        borderBottom: '1px solid #f1f5f9',
                        background: isEdited ? '#fffbeb' : 'white'
                      }}>
                        <td style={{ padding: '8px 12px', fontWeight: '500', color: '#1e293b', whiteSpace: 'nowrap' }}>
                          {dayName}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <input type="number" min={0} step={0.5} value={merged.hours_normal}
                              onChange={e => updateCorrectionField(entry.id, 'hours_normal', parseFloat(e.target.value) || 0)}
                              title="Heures normales"
                              style={{ width: '42px', textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px', fontSize: '0.8rem' }} />
                            <input type="number" min={0} step={0.5} value={merged.hours_saturday}
                              onChange={e => updateCorrectionField(entry.id, 'hours_saturday', parseFloat(e.target.value) || 0)}
                              title="Heures samedi"
                              style={{ width: '42px', textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px', fontSize: '0.8rem' }} />
                            <input type="number" min={0} step={0.5} value={merged.hours_major}
                              onChange={e => updateCorrectionField(entry.id, 'hours_major', parseFloat(e.target.value) || 0)}
                              title="Heures majorées (jours fériés)"
                              style={{ width: '42px', textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px', fontSize: '0.8rem' }} />
                          </div>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <input type="number" min={0} value={merged.meals_count}
                            onChange={e => updateCorrectionField(entry.id, 'meals_count', parseInt(e.target.value) || 0)}
                            style={{ width: '50px', textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px', fontSize: '0.85rem' }} />
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <input type="number" min={0} value={merged.indemnity_trajet}
                            onChange={e => updateCorrectionField(entry.id, 'indemnity_trajet', parseInt(e.target.value) || 0)}
                            style={{ width: '50px', textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px', fontSize: '0.85rem' }} />
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <input type="number" min={0} value={merged.indemnity_transport}
                            onChange={e => updateCorrectionField(entry.id, 'indemnity_transport', parseInt(e.target.value) || 0)}
                            style={{ width: '50px', textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px', fontSize: '0.85rem' }} />
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <select value={merged.travel_zone ?? ''}
                            onChange={e => updateCorrectionField(entry.id, 'travel_zone', e.target.value || null)}
                            style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px 6px', fontSize: '0.8rem' }}>
                            <option value="">-</option>
                            <option value="1A">1A</option>
                            <option value="1B">1B</option>
                            {[2,3,4,5,6,7].map(z => <option key={z} value={z}>{z}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <input type="checkbox" checked={merged.is_grand_deplacement}
                            onChange={e => updateCorrectionField(entry.id, 'is_grand_deplacement', e.target.checked)}
                            style={{ width: '18px', height: '18px', accentColor: '#dc2626', cursor: 'pointer' }} />
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <input type="number" min={0} step={10} value={merged.gd_amount ?? 0}
                            disabled={!merged.is_grand_deplacement}
                            onChange={e => updateCorrectionField(entry.id, 'gd_amount', parseFloat(e.target.value) || 0)}
                            style={{
                              width: '60px', textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: '6px',
                              padding: '4px', fontSize: '0.85rem',
                              opacity: merged.is_grand_deplacement ? 1 : 0.3
                            }} />
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <input type="number" min={0} step={0.5} value={merged.gd_travel_hours ?? 0}
                            disabled={!merged.is_grand_deplacement}
                            onChange={e => updateCorrectionField(entry.id, 'gd_travel_hours', parseFloat(e.target.value) || 0)}
                            style={{
                              width: '60px', textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: '6px',
                              padding: '4px', fontSize: '0.85rem',
                              opacity: merged.is_grand_deplacement ? 1 : 0.3
                            }} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Historique des modifications — une timeline par feuille concernée */}
            {correctionEntries.length > 0 && (
              <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', maxHeight: '280px', overflowY: 'auto' }}>
                {Array.from(new Set(correctionEntries.map(e => e.sheet_id).filter(Boolean))).map(sheetId => (
                  <div key={sheetId} style={{ marginBottom: '10px' }}>
                    <TimesheetAuditTrail sheetId={sheetId} compact={true} />
                  </div>
                ))}
              </div>
            )}

            {/* Footer */}
            <div style={{
              padding: '16px 24px', borderTop: '1px solid #e2e8f0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                {hasEdits ? `${Object.keys(correctionEdits).length} entrée(s) modifiée(s)` : 'Aucune modification'}
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setCorrectionEmployee(null)} style={{
                  padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0',
                  background: 'white', color: '#64748b', cursor: 'pointer', fontSize: '0.85rem'
                }}>
                  Annuler
                </button>
                <button onClick={saveCorrections} disabled={!hasEdits || savingCorrection} style={{
                  padding: '8px 20px', borderRadius: '8px', border: 'none',
                  background: hasEdits ? '#2563eb' : '#94a3b8', color: 'white',
                  cursor: hasEdits ? 'pointer' : 'not-allowed', fontSize: '0.85rem',
                  fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px'
                }}>
                  {savingCorrection ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  {savingCorrection ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Note: Removed minHeight: 100vh to integrate properly with Layout component
